import { siteCapturesQuery } from '@/api/queries'
import type { Capture } from '@/api/schemas'
import { ErrorCard, LoadingCard } from '@/components/ui/StateCards'
import { formatLocalTime } from '@/lib/format'
import type { SalaIntent } from '@/lib/viewIntent'
import { useQuery } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import { useState } from 'react'
import { t } from '@/lib/i18n'

type Intent = Extract<SalaIntent, { kind: 'photos' }>

export function PhotosBody({ intent }: { intent: Intent }) {
  const router = useRouter()
  const q = useQuery(
    siteCapturesQuery(
      intent.site_id,
      intent.date ?? null,
      intent.camera_id ?? null,
      intent.event_ids ?? null,
    ),
  )
  if (q.isPending) return <LoadingCard label={t('Loading captures…')} />
  if (q.isError || !q.data) return <ErrorCard message={t('Could not load captures.')} />
  const { captures } = q.data
  if (captures.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-card p-8 text-center text-sm text-foreground-muted">
        {t('No captures stored for that day.')}
      </div>
    )
  }

  // Clicking a thumbnail opens the familiar EventDrawer (the same side
  // panel users see elsewhere in the app) by adding `?event=<id>` to the
  // current URL. The sala stays mounted; the drawer overlays on top.
  const openEvent = (eventId: number) => {
    router.navigate({
      to: router.state.location.pathname,
      search: (prev: Record<string, unknown>) => ({ ...prev, event: eventId }),
    } as never)
  }

  // Group by camera so the grid reads by origin when multiple cameras
  // are folded into a site-wide query. For a single camera intent, the
  // group becomes one section with the camera id as the heading.
  const groups = groupByCamera(captures)

  return (
    <div className="space-y-6">
      {groups.map((g) => (
        <section key={g.cameraId}>
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
            {g.cameraId}{' '}
            <span className="text-xs font-normal text-subtle-foreground">
              · {g.items.length} capture{g.items.length === 1 ? '' : 's'}
            </span>
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {g.items.map((c) => (
              <Thumb key={c.event_id} capture={c} onClick={() => openEvent(c.event_id)} />
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}

function Thumb({ capture, onClick }: { capture: Capture; onClick: () => void }) {
  const [broken, setBroken] = useState(false)
  return (
    <button
      type="button"
      onClick={onClick}
      className="block text-left rounded-lg border border-border overflow-hidden bg-muted hover:ring-2 hover:ring-primary transition-all w-full"
    >
      <div className="aspect-video bg-background relative">
        {broken ? (
          <div className="absolute inset-0 flex items-center justify-center text-xs text-subtle-foreground">
            {t('image unavailable')}
          </div>
        ) : (
          <img
            src={capture.image_url}
            alt={`${capture.camera_id} at ${capture.captured_at}`}
            loading="lazy"
            onError={() => setBroken(true)}
            className="w-full h-full object-cover"
            onLoad={(e) => {
              const img = e.currentTarget
              if (img.naturalHeight > img.naturalWidth) {
                img.parentElement?.classList.remove('aspect-video')
                img.parentElement?.classList.add('aspect-[9/16]')
              }
            }}
          />
        )}
      </div>
      <div className="px-2 py-1 text-xs text-foreground-muted tabular-nums flex items-center justify-between">
        <span>{formatLocalTime(capture.captured_at)}</span>
      </div>
    </button>
  )
}

interface Group {
  cameraId: string
  items: Capture[]
}

function groupByCamera(captures: Capture[]): Group[] {
  const map = new Map<string, Capture[]>()
  for (const c of captures) {
    const bucket = map.get(c.camera_id)
    if (bucket) bucket.push(c)
    else map.set(c.camera_id, [c])
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([cameraId, items]) => ({ cameraId, items }))
}
